import { Button, Col, PageHeader, Row, Table, InputNumber, Select, Input, Checkbox } from 'antd'
import { refundArrowSVG } from 'assets/svg/arrow'
import { addSVG } from 'assets/svg/add'
import { DownloadSVGLink } from 'assets/svg/download'
import { QuestionIcon } from 'assets/svg/questionMark'
import { LinkButton } from 'atom/button'
import { WalletCardWrapper } from 'atom/card'
import { BackgroundContainer } from 'atom/layout/backgroundContainer'
import { DateTimeV2 } from 'components/date-time'
import { AlertError } from 'components/error'
import { withAuthenticatedLayout } from 'components/layouts/layout'
import { CancelModal } from 'components/modal/CancelModal'
import BalanceText from 'components/text/BalanceText'
import BulletPoint from 'components/text/BulletPoint'
import LabeledText from 'components/text/LabeledText'
import { useUserTranslation } from 'hooks/translation/useUserTranslation'
import { useNotifications } from 'hooks/useNotification'
import { SwtchError } from 'models/error'
import { WalletFilter } from 'models/filter'
import { PaginationMeta } from 'models/pagination'
import { WalletData, WalletInfo } from 'models/user'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  GetWalletHistory,
  GetWalletInfo,
  GetWholeWalletHistory,
  RefundBalance,
  RefundTransaction,
  AddFundsToWallet,
} from 'services/data-provider/activities'
import { useAppState } from 'state'
import { theme } from 'theme'
import WalletActivityModal from './modal/WalletActivityModal'
import { createDownloadableCSV } from 'helpers/jsonToSCV'
import { Loading } from 'atom/loader'

const UserWalletBasePage: React.FC = () => {
  document.querySelector('body')?.classList.add('redesignActive')
  // @ts-ignore
  const { userId } = useParams()

  const { isMockUpEnabled } = useAppState()

  const {
    driverWalletText,
    walletRefundModalTitleText,
    walletRefundModalDescriptionText,
    walletRefundModalBulletPoint1Text,
    walletRefundModalBulletPoint2Text,
    walletRefundModalConfirmRefundText,
    walletHistoryTableDateText,
    walletHistoryTableActivityText,
    walletHistoryTableAmountText,
    walletHistoryTableBalanceText,
    walletHistoryTableTransactionIdText,
    walletHistoryTableConnectedText,
    walletHistoryTableDisconnectedText,
    walletEmailText,
    walletCarMakeModelText,
    walletLicensePlateText,
    walletExportCSVText,
    walletCurrentBalanceText,
    walletRefundText,
    walletAddFundsText,
    walletAddFundsModalTitleText,
    walletAddFundsModalDescriptionText,
    walletAddFundsModalAmountLabelText,
    walletAddFundsModalReasonLabelText,
    walletAddFundsModalReasonPlaceholderText,
    walletAddFundsModalCustomReasonLabelText,
    walletAddFundsModalCustomReasonPlaceholderText,
    walletAddFundsModalConfirmationText,
    walletAddFundsModalBalanceText,
    walletAddFundsModalAvailableText,
    walletAddFundsModalReasonsRefundText,
    walletAddFundsModalReasonsPromotionalText,
    walletAddFundsModalReasonsOtherText,
    walletTransactionRefundModalTitleText,
    walletTransactionRefundModalDescriptionText,
    walletTransactionRefundModalTransactionDetailsText,
    walletTransactionRefundModalAmountDetailsText,
    walletTransactionRefundModalReasonLabelText,
    walletTransactionRefundModalReasonPlaceholderText,
    walletTransactionRefundModalCustomReasonLabelText,
    walletTransactionRefundModalCustomReasonPlaceholderText,
    walletTransactionRefundModalBalanceUpdateText,
    walletTransactionRefundModalIrreversibleText,
    walletTransactionRefundModalReasonsDuplicateChargeText,
    walletTransactionRefundModalReasonsIncorrectAmountText,
    walletTransactionRefundModalReasonsCustomerDisputeText,
    walletTransactionRefundModalReasonsOtherText,
    walletHistoryText,
  } = useUserTranslation()

  const [walletData, setWalletData] = useState<WalletData[]>([])
  const [error, setError] = useState<SwtchError>()
  const [walletInfo, setWalletInfo] = useState<WalletInfo>()
  const [pagination, setPagination] = useState<PaginationMeta>()
  const [filter, setFilter] = useState<WalletFilter>({ page: 1, perPage: 10 })
  const [refundModalVisible, setRefundModalVisible] = useState(false)
  const [addFundsModalVisible, setAddFundsModalVisible] = useState(false)
  const [walletActivityModalVisible, setWalletActivityModalVisible] = useState(false)
  const [refundLoading, setRefundLoading] = useState(false)
  const [addFundsLoading, setAddFundsLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [addFundsAmount, setAddFundsAmount] = useState(0)
  const [addFundsReason, setAddFundsReason] = useState('')
  const [addFundsCustomReason, setAddFundsCustomReason] = useState('')
  const [addFundsFormSubmitted, setAddFundsFormSubmitted] = useState(false)
  const [selectedTransaction, setSelectedTransaction] = useState<WalletData | null>(null)
  const [transactionRefundModalVisible, setTransactionRefundModalVisible] = useState(false)

  const { openSuccessNotification } = useNotifications()
  const walletColumns = [
    {
      title: '',
      key: 'select',
      width: 50,
      render: (record: WalletData) => {
        if (!(record.transactionId && record.transactionState === 'confirmed')) {
          return null
        }
        return (
          <Checkbox
            checked={selectedTransaction?.id === record.id}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedTransaction(record)
              } else {
                setSelectedTransaction(null)
              }
            }}
          />
        )
      },
    },
    {
      title: walletHistoryTableDateText,
      key: 'createdAt',
      render: ({ createdAt, timeZone }: WalletData) => {
        if (!createdAt) return '-'
        return (
          <DateTimeV2
            alignLeft
            date={createdAt}
            timezone={timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone}
          />
        )
      },
    },
    {
      title: () => (
        <Row align="middle">
          <span>{walletHistoryTableActivityText}</span>
          <Button type="text" style={{ padding: 0 }} icon={<QuestionIcon />} onClick={handleWalletActivityModal} />
        </Row>
      ),
      key: 'title',
      dataIndex: 'title',
    },
    {
      title: walletHistoryTableTransactionIdText,
      key: 'transactionId',
      render: ({ transactionId, createdAt }: WalletData) => {
        if (!transactionId) return '-'

        const url = `/transactions?transaction_id=${transactionId}&start_date=${createdAt}&end_date=${createdAt}`
        return (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => {
              console.log('URL in onClick:', e.currentTarget.href)
            }}
          >
            {transactionId}
          </a>
        )
      },
    },
    {
      title: walletHistoryTableConnectedText,
      key: 'connectedAt',
      render: ({ connectedAt, timeZone }: WalletData) => {
        if (!connectedAt) return '-'
        return (
          <DateTimeV2
            alignLeft
            date={connectedAt}
            timezone={timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone}
          />
        )
      },
    },
    {
      title: walletHistoryTableDisconnectedText,
      key: 'disconnectedAt',
      render: ({ disconnectedAt, timeZone }: WalletData) => {
        if (!disconnectedAt) return '-'
        return (
          <DateTimeV2
            alignLeft
            date={disconnectedAt}
            timezone={timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone}
          />
        )
      },
    },
    {
      title: walletHistoryTableAmountText,
      key: 'amount',
      align: 'right' as 'right',
      width: 100,
      render: ({ amount }: WalletData) => {
        if (amount < 0) return `($${Math.abs(parseFloat(`${amount}`)).toFixed(2)})`
        return <span style={{ color: theme.colors.successPrimary }}>${parseFloat(`${amount}`).toFixed(2)}</span>
      },
    },
    {
      title: walletHistoryTableBalanceText,
      key: 'balance',
      align: 'right' as 'right',
      width: 100,
      render: ({ balance }: WalletData) => {
        if (balance < 0) return `($${Math.abs(parseFloat(`${balance}`)).toFixed(2)})`
        return <span>${parseFloat(`${balance}`).toFixed(2)}</span>
      },
    },
  ]

  const handleRefundModal = () => setRefundModalVisible(!refundModalVisible)
  const handleAddFundsModal = () => {
    setAddFundsModalVisible(!addFundsModalVisible)
    if (addFundsModalVisible) {
      // Reset form when closing modal
      setAddFundsAmount(0)
      setAddFundsReason('')
      setAddFundsCustomReason('')
      setAddFundsFormSubmitted(false)
    }
  }

  const handleTransactionRefundModal = () => {
    setTransactionRefundModalVisible(!transactionRefundModalVisible)
    if (transactionRefundModalVisible) {
      // Reset form when closing modal
      setAddFundsAmount(0)
      setAddFundsReason('')
      setAddFundsCustomReason('')
      setAddFundsFormSubmitted(false)
      setSelectedTransaction(null)
    } else if (selectedTransaction) {
      // Pre-fill form with transaction amount when opening, but leave reason empty
      setAddFundsAmount(Math.abs(selectedTransaction.amount))
      setAddFundsReason('')
    }
  }
  const handleWalletActivityModal = () => setWalletActivityModalVisible(!walletActivityModalVisible)
  const handleAddFunds = async () => {
    setAddFundsFormSubmitted(true)

    if (addFundsAmount <= 0) {
      return
    }

    if (!addFundsReason) {
      return
    }

    if (addFundsReason === 'other' && !addFundsCustomReason.trim()) {
      return
    }

    setAddFundsLoading(true)
    try {
      const otherReason = addFundsReason === 'other' ? addFundsCustomReason : undefined

      await AddFundsToWallet(
        isMockUpEnabled,
        userId,
        addFundsAmount,
        addFundsReason,
        otherReason
      )
  
      openSuccessNotification(`Successfully added $${addFundsAmount.toFixed(2)} to wallet`)
      handleAddFundsModal()
      fetchWalletInfo() // Refresh wallet info
      fetchWalletHistory() // Refresh transaction history
    } catch (error) {
      setError(error as SwtchError)
    } finally {
      setAddFundsLoading(false)
    }
  }

  const handleTransactionRefund = async () => {
    setAddFundsFormSubmitted(true)

    if (addFundsAmount <= 0) {
      return
    }

    if (!addFundsReason) {
      return
    }

    if (addFundsReason === 'other' && !addFundsCustomReason.trim()) {
      return
    }

    // Validate that refund amount doesn't exceed transaction amount
    if (selectedTransaction && addFundsAmount > Math.abs(selectedTransaction.amount)) {
      return
    }

    if (!selectedTransaction) {
      console.error('No transaction selected for refund')
      return
    }

    setAddFundsLoading(true)
    try {
      const otherReason = addFundsReason === 'other' ? addFundsCustomReason : undefined

      await RefundTransaction(
        isMockUpEnabled,
        userId,
        String(selectedTransaction.transactionId),
        addFundsAmount,
        addFundsReason,
        otherReason
      )

      openSuccessNotification(`Successfully refunded $${addFundsAmount.toFixed(2)} for transaction ${selectedTransaction.transactionId}`)
      handleTransactionRefundModal()
      fetchWalletInfo() // Refresh wallet info
      fetchWalletHistory() // Refresh transaction history
    } catch (error) {
      setError(error as SwtchError)
    } finally {
      setAddFundsLoading(false)
    }
  }
  const onPaginationChange = (page: number) => {
    setFilter({ ...filter, page })
    fetchWalletHistory(page)
  }

  const handleRefund = async () => {
    setRefundLoading(true)
    try {
      const { message } = await RefundBalance(isMockUpEnabled, userId)
      openSuccessNotification(message)
      handleRefundModal()
      fetchWalletHistory()
      fetchWalletInfo()
    } catch (error) {
      setError(error as SwtchError)
    } finally {
      setRefundLoading(false)
    }
  }

  const formatWholeHistoryWithTableColumns = (data: WalletData[]) => {
    const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    return data.map((item) => {
      return {
        [walletHistoryTableDateText]: item.createdAt
          ? new Date(item.createdAt).toLocaleString(undefined, { timeZone: item.timeZone || localTimeZone })
          : '-',
        [walletHistoryTableActivityText]: item.title || '-',
        [walletHistoryTableTransactionIdText]: item.transactionId || '-',
        [walletHistoryTableConnectedText]: item.connectedAt
          ? new Date(item.connectedAt).toLocaleString(undefined, { timeZone: item.timeZone || localTimeZone })
          : '-',
        [walletHistoryTableDisconnectedText]: item.disconnectedAt
          ? new Date(item.disconnectedAt).toLocaleString(undefined, { timeZone: item.timeZone || localTimeZone })
          : '-',
        [walletHistoryTableAmountText]:
          item.amount < 0
            ? `($${Math.abs(parseFloat(`${item.amount}`)).toFixed(2)})`
            : `$${parseFloat(`${item.amount}`).toFixed(2)}`,
        [walletHistoryTableBalanceText]:
          item.balance < 0
            ? `($${Math.abs(parseFloat(`${item.balance}`)).toFixed(2)})`
            : `$${parseFloat(`${item.balance}`).toFixed(2)}`,
      }
    })
  }

  const handleExportCSV = async () => {
    try {
      const data = await GetWholeWalletHistory(isMockUpEnabled, userId)

      // Format data to match table columns
      const formattedData = formatWholeHistoryWithTableColumns(data)

      const csvFileURL = createDownloadableCSV(formattedData)

      // Create a link element and trigger download
      const downloadLink = document.createElement('a')
      downloadLink.href = csvFileURL
      downloadLink.download = `wallet-history-${userId}.csv` // Name for the downloaded file
      document.body.appendChild(downloadLink)
      downloadLink.click()

      // Clean up
      document.body.removeChild(downloadLink)
      URL.revokeObjectURL(csvFileURL) // Free up memory
    } catch (error) {
      setError(error as SwtchError)
    }
  }

  const fetchWalletHistory = async (page: number = 1) => {
    try {
      setLoading(true)
      const { data, pagination } = await GetWalletHistory(isMockUpEnabled, userId, page)
      setWalletData(data)
      setPagination(pagination)
    } catch (error) {
      setError(error as SwtchError)
    } finally {
      setLoading(false)
    }
  }

  const fetchWalletInfo = async () => {
    try {
      const data = await GetWalletInfo(isMockUpEnabled, userId)
      setWalletInfo(data)
    } catch (error) {
      setError(error as SwtchError)
    }
  }

  useEffect(() => {
    fetchWalletHistory()
    fetchWalletInfo()
  }, [])


  return (
    <BackgroundContainer>
      <WalletActivityModal visible={walletActivityModalVisible} onCancel={handleWalletActivityModal} />
      <CancelModal
        visible={refundModalVisible}
        onCancel={handleRefundModal}
        onSubmit={handleRefund}
        submitText={walletRefundModalConfirmRefundText}
        title={walletRefundModalTitleText}
        description={
          <div>
            <p className="paragraph-01-regular">
              {walletRefundModalDescriptionText.replace('{balance}', `$${walletInfo?.balance}`)}
            </p>
            <BulletPoint value={walletRefundModalBulletPoint1Text} />
            <BulletPoint value={walletRefundModalBulletPoint2Text} />
          </div>
        }
      />
      <CancelModal
        visible={addFundsModalVisible}
        onCancel={handleAddFundsModal}
        onSubmit={handleAddFunds}
        submitText={walletAddFundsText}
        title={walletAddFundsModalTitleText}
        icon={addSVG}
        description={
          <div>
            <p className="paragraph-01-regular" style={{ marginBottom: '24px' }}>
              {walletAddFundsModalDescriptionText}
            </p>

            <div style={{ marginBottom: '16px' }}>
              <label className="heading-06-bold-normal-opacity" style={{ display: 'block', marginBottom: '8px' }}>
                {walletAddFundsModalAmountLabelText}
              </label>
              <InputNumber
                prefix="$"
                value={addFundsAmount}
                onChange={(value) => setAddFundsAmount(value || 0)}
                style={{ width: '100%' }}
                min={0}
                step={0.01}
                precision={2}
                placeholder="0.00"
                status={addFundsFormSubmitted && addFundsAmount <= 0 ? 'error' : ''}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label className="heading-06-bold-normal-opacity" style={{ display: 'block', marginBottom: '8px' }}>
                {walletAddFundsModalReasonLabelText}
              </label>
              <Select
                value={addFundsReason}
                onChange={(value) => setAddFundsReason(value)}
                style={{ width: '100%' }}
                placeholder={walletAddFundsModalReasonPlaceholderText}
                status={addFundsFormSubmitted && !addFundsReason ? 'error' : ''}
                options={[
                  { value: 'refund_for_overcharge', label: walletAddFundsModalReasonsRefundText },
                  { value: 'promotional_credit', label: walletAddFundsModalReasonsPromotionalText },
                  { value: 'other', label: walletAddFundsModalReasonsOtherText }
                ]}
              />

              {addFundsReason === 'other' && (
                <div style={{ marginTop: '16px' }}>
                  <label className="heading-06-bold-normal-opacity" style={{ display: 'block', marginBottom: '8px' }}>
                    {walletAddFundsModalCustomReasonLabelText}
                  </label>
                  <Input
                    value={addFundsCustomReason}
                    onChange={(e) => setAddFundsCustomReason(e.target.value)}
                    placeholder={walletAddFundsModalCustomReasonPlaceholderText}
                    style={{ width: '100%' }}
                    status={addFundsFormSubmitted && addFundsReason === 'other' && !addFundsCustomReason.trim() ? 'error' : ''}
                  />
                </div>
              )}
            </div>

            <div style={{ marginBottom: '16px' }}>
              <p className="paragraph-01-regular">
                {walletAddFundsModalConfirmationText.replace('${amount}', `$${addFundsAmount.toFixed(2)}`)}
              </p>
              <BulletPoint value={walletAddFundsModalBalanceText.replace('${balance}', `$${((walletInfo?.balance || 0) + addFundsAmount).toFixed(2)}`)} />
              <BulletPoint value={walletAddFundsModalAvailableText} />
            </div>
          </div>
        }
      />
      <CancelModal
        visible={transactionRefundModalVisible}
        onCancel={handleTransactionRefundModal}
        onSubmit={handleTransactionRefund}
        submitText="REFUND TRANSACTION"
        title={walletTransactionRefundModalTitleText}
        icon={refundArrowSVG}
        description={
          <div>
            <p className="paragraph-01-regular" style={{ marginBottom: '24px' }}>
              {walletTransactionRefundModalDescriptionText}
            </p>

            <div style={{ marginBottom: '16px' }}>
              <p className="paragraph-01-regular">
                {walletTransactionRefundModalTransactionDetailsText.replace('{transactionId}', String(selectedTransaction?.transactionId || ''))}
              </p>
              <p className="paragraph-01-regular">
                {walletTransactionRefundModalAmountDetailsText.replace('${amount}', `$${Math.abs(selectedTransaction?.amount || 0).toFixed(2)}`)}
              </p>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label className="heading-06-bold-normal-opacity" style={{ display: 'block', marginBottom: '8px' }}>
                {walletTransactionRefundModalReasonLabelText}
              </label>
              <Select
                value={addFundsReason}
                onChange={(value) => setAddFundsReason(value)}
                style={{ width: '100%' }}
                placeholder={walletTransactionRefundModalReasonPlaceholderText}
                status={addFundsFormSubmitted && !addFundsReason ? 'error' : ''}
                options={[
                  { value: 'duplicate_charge', label: walletTransactionRefundModalReasonsDuplicateChargeText },
                  { value: 'incorrect_amount', label: walletTransactionRefundModalReasonsIncorrectAmountText },
                  { value: 'customer_dispute', label: walletTransactionRefundModalReasonsCustomerDisputeText },
                  { value: 'other', label: walletTransactionRefundModalReasonsOtherText }
                ]}
              />

              {addFundsReason === 'other' && (
                <div style={{ marginTop: '16px' }}>
                  <label className="heading-06-bold-normal-opacity" style={{ display: 'block', marginBottom: '8px' }}>
                    {walletTransactionRefundModalCustomReasonLabelText}
                  </label>
                  <Input
                    value={addFundsCustomReason}
                    onChange={(e) => setAddFundsCustomReason(e.target.value)}
                    placeholder={walletTransactionRefundModalCustomReasonPlaceholderText}
                    style={{ width: '100%' }}
                    status={addFundsFormSubmitted && addFundsReason === 'other' && !addFundsCustomReason.trim() ? 'error' : ''}
                  />
                </div>
              )}
            </div>

            <div style={{ marginBottom: '16px' }}>
              <BulletPoint value={walletTransactionRefundModalBalanceUpdateText.replace('${amount}', `$${Math.abs(selectedTransaction?.amount || 0).toFixed(2)}`)} />
              <BulletPoint value={walletTransactionRefundModalIrreversibleText} />
            </div>
          </div>
        }
      />
      <PageHeader title={<h4 className="heading-04-regular">{driverWalletText}</h4>} />
      {walletInfo ? (
        <Row gutter={[24, 24]} style={{ marginBottom: '24px' }}>
          <Col sm={12} style={{ display: 'flex' }}>
            <WalletCardWrapper>
              <span className="heading-06-bold-normal-opacity">{walletCurrentBalanceText}</span>
              <Row style={{ alignItems: 'flex-end', gap: '8px' }}>
                <BalanceText balance={walletInfo?.balance} currency={walletInfo?.currency.toUpperCase()} />
              </Row>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button
                  type="default"
                  className="regular-cap-2"
                  icon={addSVG}
                  onClick={handleAddFundsModal}
                  loading={addFundsLoading}
                >
                  {walletAddFundsText}
                </Button>
                <Button
                  disabled={walletInfo?.balance <= 0 || refundLoading}
                  type="primary"
                  className="regular-cap-2"
                  icon={refundArrowSVG}
                  onClick={handleRefundModal}
                  loading={refundLoading}
                >
                  {walletRefundText}
                </Button>
              </div>
            </WalletCardWrapper>
          </Col>
          <Col sm={12} style={{ display: 'flex' }}>
            <WalletCardWrapper>
              <LabeledText showBorderBottom label={walletEmailText} value={walletInfo.email} />
              <Row>
                <LabeledText sm={12} noMarginBottom label={walletCarMakeModelText} value={walletInfo.carMakeModel} />
                <LabeledText sm={12} noMarginBottom label={walletLicensePlateText} value={walletInfo.licensePlate} />
              </Row>
            </WalletCardWrapper>
          </Col>
        </Row>
      ) : (
        <Row>
          <Col span={24}>
            <Loading />
          </Col>
        </Row>
      )}
      <PageHeader
        title={<h4 className="heading-04-regular">{walletHistoryText}</h4>}
        extra={
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {selectedTransaction && (
              <Button
                type="primary"
                className="regular-cap-2"
                onClick={handleTransactionRefundModal}
                style={{
                  backgroundColor: theme.colors.primary,
                  borderColor: theme.colors.primary,
                  color: theme.colors.ternary
                }}
              >
                WALLET REFUND
              </Button>
            )}
            <LinkButton
              text={walletExportCSVText}
              onClick={handleExportCSV}
              icon={<DownloadSVGLink width={15} height={16} color={theme.colors.primary} />}
            />
          </div>
        }
      />
      <Table
        columns={walletColumns}
        dataSource={walletData}
        loading={loading}
        rowKey="id"
        className="pagination-white"
        pagination={{
          position: ['bottomCenter'],
          total: pagination?.totalEntries,
          current: pagination?.currentPage,
          pageSize: pagination?.perPage,
          showSizeChanger: false,
          onChange: onPaginationChange,
        }}
      />
    </BackgroundContainer>
  )
}

export const UserWalletPage = withAuthenticatedLayout(UserWalletBasePage)
